import React from 'react';
import { Link, useParams } from 'react-router-dom';
import articles from './articles';
import { getArticleLocale } from './types';
import { useLanguage } from '../i18n/LanguageContext';
import { useDynamicSEO } from '../hooks/useSEO';
import { NotFoundPage } from '../components/ErrorPages';
import { AdBanner, AdSideRails, SLOT_HOMEPAGE_TOP, SLOT_HOMEPAGE_BOTTOM } from '../components/ads/Ads';
import './blog.css';

// Article page (/blog/:slug). Renders the article's HTML body in the current
// UI language (fallback: English → any available) and injects the article's
// optional custom CSS while it is on screen.
const BlogArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { lang, t } = useLanguage();

  const article = articles.find((a) => a.slug === slug);
  const locale = article ? getArticleLocale(article, lang) : undefined;

  useDynamicSEO(
    locale ? `${locale.title} | StudyMind` : 'StudyMind Blog',
    locale?.excerpt,
    locale?.keywords
  );

  if (!article || !locale) return <NotFoundPage />;

  const dateLocale = lang === 'en' ? 'en-GB' : lang === 'zhHant' ? 'zh-HK' : 'zh-CN';

  return (
    <div className="blog-page">
      {/* Per-article custom CSS (declared in the article file) */}
      {article.css && <style>{article.css}</style>}

      {/* Fixed skyscraper rails on wide screens (overlay, never shifts content) */}
      <AdSideRails />

      <Link to="/blog" className="blog-back-link">
        {t('blog.back')}
      </Link>

      <article>
        <header className="blog-article-header">
          <h1>{locale.title}</h1>
          <div className="blog-meta">
            <span>
              {new Date(article.date).toLocaleDateString(dateLocale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
            <span>
              {article.readingMinutes} {t('blog.minRead')}
            </span>
            {article.tags.map((tag) => (
              <span key={tag} className="blog-tag">
                {tag}
              </span>
            ))}
          </div>
        </header>

        {/* Top leaderboard — right below the title/meta, before the body */}
        <AdBanner label="article-top" slot={SLOT_HOMEPAGE_TOP} leaderboard style={{ margin: '0 auto 28px' }} />

        {/* Author-written article HTML (trusted, first-party content) */}
        <div
          className="blog-article-content"
          dangerouslySetInnerHTML={{ __html: locale.contentHtml }}
        />

        {/* Bottom leaderboard — catches readers who finish the article */}
        <AdBanner label="article-bottom" slot={SLOT_HOMEPAGE_BOTTOM} leaderboard style={{ margin: '32px auto 0' }} />
      </article>
    </div>
  );
};

export default BlogArticle;
